import React, { useState, useEffect, useRef } from 'react';
import { collection, onSnapshot, query, orderBy, where, addDoc } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { UserProfile, ChatMessage } from '../types';
import { Clock, Send } from 'lucide-react';
import { safeFormatDate } from '../lib/utils';
import { Input } from './ui/input';
import { Button } from './ui/button';

interface EventChatProps {
  eventId: string;
  eventName: string;
  currentUserProfile: UserProfile;
}

export default function EventChat({ eventId, eventName, currentUserProfile }: EventChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'messages'),
      where('channelId', '==', `event_${eventId}`),
      orderBy('timestamp', 'asc')
    );

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as ChatMessage));
      setMessages(data);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'messages');
    });

    return () => unsub();
  }, [eventId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || !currentUserProfile?.uid) return;

    const newMsg = {
      channelId: `event_${eventId}`,
      userId: currentUserProfile.uid,
      userName: `${currentUserProfile.firstName} ${currentUserProfile.lastName}`,
      text: inputText.trim(),
      timestamp: new Date().toISOString()
    };

    setInputText('');

    try {
      await addDoc(collection(db, 'messages'), newMsg);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'messages');
    }
  };

  return (
    <div className="flex flex-col h-[500px] bg-[var(--background)] border-2 border-lcars-blue rounded-xl overflow-hidden mt-6">
      <div className="bg-lcars-blue text-black p-4 lcars-text-large flex justify-between items-center">
        <span className="uppercase">{eventName} - MISSION BRIEFING</span>
        <span className="hidden sm:inline text-sm font-sans tracking-normal opacity-80">
          <Clock className="w-4 h-4 inline mr-2" />
          ENCRYPTED
        </span>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8 text-[var(--foreground)]/40 tracking-widest text-sm uppercase">
            No entries in this mission briefing yet.
          </div>
        )}
        {messages.map(msg => {
          const isMe = msg.userId === currentUserProfile?.uid;
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[90%] sm:max-w-[80%] rounded-xl px-4 py-3 ${isMe ? 'bg-lcars-gold/20 border border-lcars-gold' : 'bg-white/5 border border-white/20'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-bold uppercase tracking-widest ${isMe ? 'text-lcars-gold' : 'text-lcars-blue'}`}>
                    {msg.userName}
                  </span>
                  <span className="text-[0.6rem] text-[var(--foreground)]/40">
                    {safeFormatDate(msg.timestamp, 'HH:mm', '00:00')}
                  </span>
                </div>
                <p className="text-[var(--foreground)]/90 font-sans tracking-normal">{msg.text}</p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 bg-white/5 border-t border-white/10">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <Input 
            className="flex-1 bg-[var(--background)] border-white/20 text-[var(--foreground)] placeholder:text-[var(--foreground)]/30 font-sans tracking-normal"
            placeholder="Enter transmission..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />
          <Button type="submit" disabled={!inputText.trim()} className="bg-lcars-gold hover:bg-lcars-gold/80 text-black px-4">
            <Send className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">SEND</span>
          </Button>
        </form>
      </div>
    </div>
  );
}
